const EVENT_NAME = 'blurams:traffic_origin';
const SESSION_KEY = 'blurams_traffic_origin_published_v1';
const MAX_PUBLISH_ATTEMPTS = 20;
const PUBLISH_RETRY_MS = 250;

const AI_PLATFORMS = {
  'chatgpt.com': 'chatgpt',
  'chat.openai.com': 'chatgpt',
  'perplexity.ai': 'perplexity',
  'claude.ai': 'claude',
  'gemini.google.com': 'gemini',
  'copilot.microsoft.com': 'copilot',
  'poe.com': 'poe',
  'you.com': 'you',
};

const SEARCH_PLATFORMS = {
  'google.com': 'google',
  'bing.com': 'bing',
  'search.yahoo.com': 'yahoo',
  'duckduckgo.com': 'duckduckgo',
  'baidu.com': 'baidu',
  'yandex.com': 'yandex',
};

const SOCIAL_PLATFORMS = {
  'facebook.com': 'facebook',
  'instagram.com': 'instagram',
  'tiktok.com': 'tiktok',
  'youtube.com': 'youtube',
  'linkedin.com': 'linkedin',
  'pinterest.com': 'pinterest',
  'reddit.com': 'reddit',
  'x.com': 'x',
  'twitter.com': 'x',
};

const PAID_SEARCH_MEDIA = new Set(['cpc', 'ppc', 'paid_search', 'paidsearch']);
const PAID_SOCIAL_MEDIA = new Set(['paid_social', 'paidsocial']);
const ORGANIC_SOCIAL_MEDIA = new Set(['social', 'social_media', 'organic_social', 'organicsocial']);
const EMAIL_MEDIA = new Set(['email', 'e-mail']);

/**
 * Returns a known platform for a hostname, including subdomains.
 * @param {string} hostname
 * @param {Record<string, string>} platforms
 */
function platformForHost(hostname, platforms) {
  const normalizedHost = hostname.toLowerCase().replace(/^www\./, '');

  for (const [domain, platform] of Object.entries(platforms)) {
    if (normalizedHost === domain || normalizedHost.endsWith(`.${domain}`)) return platform;
  }

  return '';
}

/**
 * Keeps campaign parameters low-risk and bounded before publishing.
 * @param {string | null} value
 */
function sanitizeCampaignValue(value) {
  if (!value) return '';

  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._~+-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 100);
}

/**
 * Classifies one landing request without retaining full URLs or click IDs.
 * Google AI Overviews cannot be reliably separated from ordinary Google search.
 * @param {{ locationUrl: string, referrer?: string, pageType?: string }} input
 */
export function classifyTrafficOrigin({ locationUrl, referrer = '', pageType = '' }) {
  const location = new URL(locationUrl);
  const params = location.searchParams;
  const utmSource = sanitizeCampaignValue(params.get('utm_source'));
  const utmMedium = sanitizeCampaignValue(params.get('utm_medium'));
  const utmCampaign = sanitizeCampaignValue(params.get('utm_campaign'));
  const hasSearchClickId = params.has('gclid') || params.has('msclkid');
  const hasSocialClickId = params.has('fbclid') || params.has('ttclid');
  const hasAdClickId = hasSearchClickId || hasSocialClickId;

  let referrerHost = '';
  try {
    referrerHost = referrer ? new URL(referrer).hostname.toLowerCase().replace(/^www\./, '') : '';
  } catch (_error) {
    referrerHost = '';
  }

  const aiFromReferrer = platformForHost(referrerHost, AI_PLATFORMS);
  const searchFromReferrer = platformForHost(referrerHost, SEARCH_PLATFORMS);
  const socialFromReferrer = platformForHost(referrerHost, SOCIAL_PLATFORMS);
  const aiFromUtm = platformForHost(utmSource, AI_PLATFORMS) ||
    Object.values(AI_PLATFORMS).find((platform) => platform === utmSource) || '';
  const socialFromUtm = Object.values(SOCIAL_PLATFORMS).find((platform) => platform === utmSource) || '';

  let originCategory = 'direct';
  let originPlatform = 'direct';
  let attributionMethod = 'direct';

  if (utmSource || utmMedium || utmCampaign) {
    attributionMethod = 'utm';
    originPlatform = aiFromUtm || socialFromUtm || utmSource || 'other';

    if (aiFromUtm) originCategory = 'ai_referral';
    else if (PAID_SEARCH_MEDIA.has(utmMedium)) originCategory = 'paid_search';
    else if (PAID_SOCIAL_MEDIA.has(utmMedium)) originCategory = 'paid_social';
    else if (ORGANIC_SOCIAL_MEDIA.has(utmMedium)) originCategory = 'organic_social';
    else if (EMAIL_MEDIA.has(utmMedium)) originCategory = 'email';
    else if (utmMedium === 'organic') originCategory = 'organic_search';
    else if (utmMedium === 'referral') originCategory = 'referral';
    else originCategory = 'other_campaign';
  } else if (hasAdClickId) {
    attributionMethod = 'click_id';
    originCategory = hasSocialClickId ? 'paid_social' : 'paid_search';
    originPlatform = params.has('gclid') ? 'google' : params.has('msclkid') ? 'bing' : params.has('fbclid') ? 'facebook' : 'tiktok';
  } else if (referrerHost) {
    attributionMethod = 'referrer';

    if (referrerHost === location.hostname.toLowerCase().replace(/^www\./, '')) {
      originCategory = 'internal';
      originPlatform = 'internal';
    } else if (aiFromReferrer) {
      originCategory = 'ai_referral';
      originPlatform = aiFromReferrer;
    } else if (searchFromReferrer) {
      originCategory = 'organic_search';
      originPlatform = searchFromReferrer;
    } else if (socialFromReferrer) {
      originCategory = 'organic_social';
      originPlatform = socialFromReferrer;
    } else {
      originCategory = 'referral';
      originPlatform = referrerHost;
    }
  }

  return {
    schema_version: '1.0',
    origin_category: originCategory,
    origin_platform: originPlatform,
    attribution_method: attributionMethod,
    referrer_host: referrerHost,
    landing_path: location.pathname,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    has_ad_click_id: hasAdClickId,
    is_ai_referral: originCategory === 'ai_referral',
    page_type: sanitizeCampaignValue(pageType),
  };
}

function hasPublishedThisSession() {
  try {
    return window.sessionStorage.getItem(SESSION_KEY) === '1';
  } catch (_error) {
    return false;
  }
}

function markPublishedThisSession() {
  try {
    window.sessionStorage.setItem(SESSION_KEY, '1');
  } catch (_error) {
    // The event can still be published when storage is unavailable.
  }
}

let hasDispatchedDebugEvent = false;

function publishTrafficOrigin(attempt = 0) {
  if (hasPublishedThisSession()) return;

  const pageType = document.querySelector('main[data-template]')?.getAttribute('data-template') || '';
  const payload = classifyTrafficOrigin({
    locationUrl: window.location.href,
    referrer: document.referrer,
    pageType,
  });

  // This DOM event exists only for local QA and does not send data externally.
  if (!hasDispatchedDebugEvent) {
    document.dispatchEvent(new CustomEvent('blurams:traffic-origin', { detail: payload }));
    hasDispatchedDebugEvent = true;
  }

  if (window.Shopify?.analytics?.publish) {
    Promise.resolve(window.Shopify.analytics.publish(EVENT_NAME, payload))
      .then(markPublishedThisSession)
      .catch((error) => console.warn('Unable to publish Blurams traffic origin event.', error));
    return;
  }

  if (attempt < MAX_PUBLISH_ATTEMPTS) {
    window.setTimeout(() => publishTrafficOrigin(attempt + 1), PUBLISH_RETRY_MS);
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => publishTrafficOrigin(), { once: true });
  } else {
    publishTrafficOrigin();
  }
}
